// Obsidian-style force-graph model for the CogniMemo memory view.
//
// ORG scope   → company → department → people → agents, with memories hanging off
//               their owner (person / department / company). Departments become the
//               separated blobs; people cluster inside; memories dot around them.
// PERSON scope → the person at the centre with one cluster per "thing" (topic, from
//               a memory's department tag, else its fact-type), memories under each.
//
// Output is {nodes, links} for react-force-graph-2d. Clustering is emergent: tree
// links pull children toward their parent, so the force sim settles into readable
// department/topic blobs rather than a hairball.
import type { OrgMember } from "../../orgMembers";
import type { MorrowMemory } from "../../morrowMemory";

export type ForceNodeKind = "company" | "department" | "person" | "agent" | "memory" | "topic";

export interface ForceNode {
  id: string;
  kind: ForceNodeKind;
  label: string;
  /** Base render radius; the graph scales it up a touch by degree. */
  size: number;
  color: string;
  parentId: string | null;
  /** The underlying memory, when kind === "memory" (for the detail panel). */
  memory?: MorrowMemory;
  /** The underlying member, when kind === "person". */
  member?: OrgMember;
  degree?: number;
}

export interface ForceLink {
  source: string;
  target: string;
  /** tree = hierarchy (strong pull); ref = cross-cluster reference (weak). */
  kind: "tree" | "ref";
}

export interface ForceGraphData {
  nodes: ForceNode[];
  links: ForceLink[];
}

// Fact types, color-coded (light-mode jewel tones) + human labels for the legend.
// Every memory node is colored by its fact type so the whole taxonomy reads at a
// glance; the legend shows all of them even when some aren't present yet.
export const FACT_TYPE_META: ReadonlyArray<{ type: string; label: string; color: string }> = [
  { type: "world", label: "Facts", color: "#2563eb" },
  { type: "experience", label: "Events", color: "#0891b2" },
  { type: "observation", label: "Observations", color: "#7c3aed" },
  { type: "procedure", label: "Procedures", color: "#059669" },
  { type: "reasoning", label: "Reasoning", color: "#d97706" },
  { type: "preference", label: "Preferences", color: "#db2777" },
  { type: "correction", label: "Corrections", color: "#dc2626" },
  { type: "profile", label: "Profile", color: "#4f46e5" },
];
const FACT_COLOR = new Map(FACT_TYPE_META.map((f) => [f.type, f.color]));
const DEFAULT_FACT_COLOR = "#64748b";
export const factTypeColor = (type?: string | null): string =>
  (type && FACT_COLOR.get(type.trim().toLowerCase())) || DEFAULT_FACT_COLOR;

// Structural (non-memory) node colors — neutral so the fact-type dots pop.
const KIND_COLOR: Record<ForceNodeKind, string> = {
  company: "#0f172a",
  department: "#334155",
  topic: "#334155",
  person: "#64748b",
  agent: "#0d9488",
  memory: DEFAULT_FACT_COLOR,
};
const KIND_SIZE: Record<ForceNodeKind, number> = {
  company: 12,
  department: 8,
  topic: 8,
  person: 5.5,
  agent: 4.5,
  memory: 2.6,
};

const GENERAL = "General";
const slug = (v: string): string =>
  v
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
const deptOf = (m: OrgMember): string => m.functionRole?.trim() || m.title?.trim() || GENERAL;
const emailKey = (v: string | null | undefined): string => (v ?? "").trim().toLowerCase();

/** Truncate a memory's text for a node label. */
const memoryLabel = (m: MorrowMemory): string =>
  m.text.length > 60 ? `${m.text.slice(0, 60)}…` : m.text;

export function buildForceGraph(input: {
  readonly scope: "org" | "personal" | "all";
  readonly companyName: string;
  readonly members: readonly OrgMember[];
  readonly memories: readonly MorrowMemory[];
}): ForceGraphData {
  const nodes: ForceNode[] = [];
  const links: ForceLink[] = [];
  const push = (n: ForceNode) => nodes.push(n);
  const link = (source: string, target: string, kind: "tree" | "ref" = "tree") =>
    links.push({ source, target, kind });

  if (input.scope === "org" || input.scope === "all") {
    // ---- ORG: company → department → people → agents, memories on their owner ----
    const companyId = "company";
    push({
      id: companyId,
      kind: "company",
      label: input.companyName.trim() || "Company",
      size: KIND_SIZE.company,
      color: KIND_COLOR.company,
      parentId: null,
    });

    const joined = input.members.filter((m) => m.joined);

    // Departments from members + any dept-tagged memory.
    const deptNames = new Set<string>();
    for (const m of joined) deptNames.add(deptOf(m));
    for (const mem of input.memories)
      if (mem.department?.trim()) deptNames.add(mem.department.trim());
    if (deptNames.size === 0) deptNames.add(GENERAL);
    const deptId = new Map<string, string>();
    for (const name of deptNames) {
      const id = `dept:${slug(name) || "general"}`;
      deptId.set(name.toLowerCase(), id);
      push({
        id,
        kind: "department",
        label: name,
        size: KIND_SIZE.department,
        color: KIND_COLOR.department,
        parentId: companyId,
      });
      link(companyId, id);
    }
    const resolveDept = (name?: string | null): string =>
      (name && deptId.get(name.trim().toLowerCase())) ||
      deptId.get(GENERAL.toLowerCase()) ||
      [...deptId.values()][0]!;

    // People under their department; remember by email so memories can attach.
    const personByEmail = new Map<string, string>();
    for (const m of joined) {
      const id = `person:${slug(m.email)}`;
      personByEmail.set(emailKey(m.email), id);
      push({
        id,
        kind: "person",
        label: m.name?.trim() || m.email,
        size: KIND_SIZE.person,
        color: KIND_COLOR.person,
        parentId: resolveDept(deptOf(m)),
        member: m,
      });
      link(resolveDept(deptOf(m)), id);
    }

    // Memories: attach to their owner person → else their department → else company.
    for (const mem of input.memories) {
      const owner = personByEmail.get(emailKey(mem.ownerEmail));
      const parent = owner ?? (mem.department ? resolveDept(mem.department) : companyId);
      const id = `mem:${mem.id}`;
      push({
        id,
        kind: "memory",
        label: memoryLabel(mem),
        size: KIND_SIZE.memory,
        color: factTypeColor(mem.type),
        parentId: parent,
        memory: mem,
      });
      link(parent, id);
    }
    // NOTE: agents (kind "agent") are a first-class level here but need a data
    // source wired to the panel (per-person agents). Rendered automatically once
    // agent nodes are added with parentId = their person id.
  } else {
    // ---- PERSON: you → one cluster per "thing" (topic = dept tag, else fact-type) ----
    const meId = "me";
    push({
      id: meId,
      kind: "person",
      label: "You",
      size: KIND_SIZE.company,
      color: KIND_COLOR.person,
      parentId: null,
    });
    const topicId = new Map<string, string>();
    const topicOf = (mem: MorrowMemory): string =>
      mem.department?.trim() || mem.type?.trim() || "general";
    for (const mem of input.memories) {
      const topic = topicOf(mem);
      let tid = topicId.get(topic.toLowerCase());
      if (!tid) {
        tid = `topic:${slug(topic) || "general"}`;
        topicId.set(topic.toLowerCase(), tid);
        push({
          id: tid,
          kind: "topic",
          label: topic,
          size: KIND_SIZE.topic,
          color: KIND_COLOR.topic,
          parentId: meId,
        });
        link(meId, tid);
      }
      const id = `mem:${mem.id}`;
      push({
        id,
        kind: "memory",
        label: memoryLabel(mem),
        size: KIND_SIZE.memory,
        color: factTypeColor(mem.type),
        parentId: tid,
        memory: mem,
      });
      link(tid, id);
    }
  }

  // Degree → nudges render size so well-connected hubs read bigger (Obsidian-like).
  const degree = new Map<string, number>();
  for (const l of links) {
    degree.set(l.source, (degree.get(l.source) ?? 0) + 1);
    degree.set(l.target, (degree.get(l.target) ?? 0) + 1);
  }
  for (const n of nodes) n.degree = degree.get(n.id) ?? 0;

  return { nodes, links };
}
